import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Send, MessageSquare, Search, User, Building2, Clock } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useAuth, API } from '../App';
import axios from 'axios';
import { toast } from 'sonner';

export default function Messages() {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const openWith = searchParams.get('with');
  const openName = searchParams.get('name');

  const [conversations, setConversations] = useState([]);
  const [activeConv, setActiveConv] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const messagesEndRef = useRef(null);
  const pollRef = useRef(null);

  const fetchConversations = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/messages/conversations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setConversations(res.data);
      return res.data;
    } catch (err) { console.error(err); return []; }
  }, [token]);

  useEffect(() => {
    const init = async () => {
      const convs = await fetchConversations();
      setLoading(false);

      // Auto-open conversation if ?with= param
      if (openWith && convs.length > 0) {
        const existing = convs.find(c => c.participants.includes(openWith));
        if (existing) {
          setActiveConv(existing);
          loadMessages(existing.id);
        }
      }
    };
    init();
  }, [fetchConversations, openWith]);

  // Auto-start new conversation if ?with= and no existing conv
  useEffect(() => {
    if (!loading && openWith && !activeConv) {
      const existing = conversations.find(c => c.participants.includes(openWith));
      if (!existing && openName) {
        // Show empty conv state - user types first message to start
        setActiveConv({
          id: null,
          participants: [user?.id, openWith],
          participant_names: { [openWith]: openName },
          participant_types: {},
          new: true,
        });
      }
    }
  }, [loading, openWith, openName, conversations, activeConv, user]);

  // Poll for new messages every 5 seconds
  useEffect(() => {
    if (activeConv?.id) {
      pollRef.current = setInterval(async () => {
        try {
          const res = await axios.get(`${API}/messages/conversations/${activeConv.id}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setMessages(res.data);
        } catch {}
      }, 5000);
    }
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [activeConv, token]);

  const loadMessages = async (convId) => {
    try {
      const res = await axios.get(`${API}/messages/conversations/${convId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessages(res.data);
      // Refresh conversations to update unread counts
      fetchConversations();
    } catch (err) { toast.error('Failed to load messages'); }
  };

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = async () => {
    if (!newMessage.trim()) return;
    setSending(true);
    try {
      if (activeConv?.new) {
        // Start new conversation
        const res = await axios.post(`${API}/messages/conversations`, {
          recipient_id: openWith,
          message: newMessage.trim()
        }, { headers: { Authorization: `Bearer ${token}` } });
        setActiveConv(res.data);
        loadMessages(res.data.id);
        fetchConversations();
      } else {
        await axios.post(`${API}/messages/conversations/${activeConv.id}`, {
          content: newMessage.trim()
        }, { headers: { Authorization: `Bearer ${token}` } });
        loadMessages(activeConv.id);
      }
      setNewMessage('');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to send message'); }
    setSending(false);
  };

  const getOtherParticipant = (conv) => {
    const otherId = conv.participants.find(p => p !== user?.id);
    return {
      id: otherId,
      name: conv.participant_names?.[otherId] || 'Unknown',
      type: conv.participant_types?.[otherId] || 'unknown',
    };
  };

  const filteredConvs = conversations.filter(c => {
    if (!searchTerm) return true;
    const other = getOtherParticipant(c);
    return other.name.toLowerCase().includes(searchTerm.toLowerCase());
  });

  const formatTime = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    const now = new Date();
    const diff = now - d;
    if (diff < 60000) return 'Just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    return d.toLocaleDateString();
  };

  const dashboardPath = user?.user_type === 'trainer' ? '/trainer/dashboard' : user?.user_type === 'corporate' ? '/corporate/dashboard' : '/';

  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="animate-spin w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full" /></div>;

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-6xl mx-auto px-4 py-6">
        <button onClick={() => navigate(dashboardPath)} className="flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-4 transition-colors" data-testid="back-to-dashboard">
          <ArrowLeft className="w-4 h-4" /> Back to Dashboard
        </button>

        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" style={{ height: 'calc(100vh - 140px)' }}>
          <div className="flex h-full">
            {/* Sidebar - Conversations List */}
            <div className={`w-80 border-r border-slate-200 flex flex-col shrink-0 ${activeConv && 'hidden md:flex'}`} data-testid="conversations-list">
              <div className="p-4 border-b border-slate-200">
                <h2 className="font-outfit text-lg font-bold text-slate-900 mb-3">Messages</h2>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input placeholder="Search conversations..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="pl-9 h-9 text-sm" data-testid="search-conversations" />
                </div>
              </div>
              <div className="flex-1 overflow-y-auto">
                {filteredConvs.length === 0 ? (
                  <div className="p-8 text-center">
                    <MessageSquare className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                    <p className="text-sm text-slate-500">No conversations yet</p>
                    <p className="text-xs text-slate-400 mt-1">Start one from a trainer or company profile</p>
                  </div>
                ) : (
                  filteredConvs.map(conv => {
                    const other = getOtherParticipant(conv);
                    const isActive = activeConv?.id === conv.id;
                    return (
                      <div
                        key={conv.id}
                        onClick={() => { setActiveConv(conv); loadMessages(conv.id); }}
                        className={`flex items-center gap-3 p-4 cursor-pointer border-b border-slate-50 transition-colors ${isActive ? 'bg-blue-50 border-l-2 border-l-blue-600' : 'hover:bg-slate-50'}`}
                        data-testid={`conversation-${conv.id}`}
                      >
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-sm shrink-0 ${other.type === 'trainer' ? 'bg-blue-500' : 'bg-emerald-500'}`}>
                          {other.name.charAt(0).toUpperCase()}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between">
                            <span className="font-medium text-sm text-slate-900 truncate">{other.name}</span>
                            <span className="text-xs text-slate-400 shrink-0">{formatTime(conv.last_message_at)}</span>
                          </div>
                          <div className="flex items-center justify-between mt-0.5">
                            <p className="text-xs text-slate-500 truncate">{conv.last_message || 'No messages'}</p>
                            {conv.unread_count > 0 && (
                              <span className="ml-2 w-5 h-5 bg-blue-600 text-white text-xs rounded-full flex items-center justify-center shrink-0" data-testid={`unread-${conv.id}`}>
                                {conv.unread_count}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>

            {/* Main Chat Area */}
            <div className={`flex-1 flex flex-col ${!activeConv && 'hidden md:flex'}`}>
              {activeConv ? (
                <>
                  {/* Chat Header */}
                  <div className="p-4 border-b border-slate-200 flex items-center gap-3">
                    <button onClick={() => setActiveConv(null)} className="md:hidden text-slate-600">
                      <ArrowLeft className="w-5 h-5" />
                    </button>
                    {(() => {
                      const other = getOtherParticipant(activeConv);
                      return (
                        <>
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-bold ${other.type === 'trainer' ? 'bg-blue-500' : 'bg-emerald-500'}`}>
                            {other.name.charAt(0).toUpperCase()}
                          </div>
                          <div>
                            <h3 className="font-semibold text-slate-900" data-testid="chat-recipient-name">{other.name}</h3>
                            <span className="text-xs text-slate-500 flex items-center gap-1">
                              {other.type === 'trainer' ? <User className="w-3 h-3" /> : <Building2 className="w-3 h-3" />}
                              {other.type}
                            </span>
                          </div>
                        </>
                      );
                    })()}
                  </div>

                  {/* Messages */}
                  <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="messages-area">
                    {messages.length === 0 && !activeConv?.new && (
                      <div className="text-center text-slate-400 text-sm py-12">No messages yet</div>
                    )}
                    {activeConv?.new && messages.length === 0 && (
                      <div className="text-center text-slate-400 text-sm py-12">
                        <MessageSquare className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                        Send your first message to start the conversation
                      </div>
                    )}
                    {messages.map(msg => {
                      const isMine = msg.sender_id === user?.id;
                      return (
                        <div key={msg.id} className={`flex ${isMine ? 'justify-end' : 'justify-start'}`} data-testid={`message-${msg.id}`}>
                          <div className={`max-w-[70%] rounded-2xl px-4 py-2.5 ${isMine ? 'bg-blue-600 text-white rounded-br-md' : 'bg-slate-100 text-slate-900 rounded-bl-md'}`}>
                            <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                            <div className={`flex items-center gap-1 mt-1 ${isMine ? 'justify-end' : ''}`}>
                              <Clock className="w-3 h-3 opacity-50" />
                              <span className={`text-xs ${isMine ? 'text-blue-100' : 'text-slate-400'}`}>{formatTime(msg.created_at)}</span>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                    <div ref={messagesEndRef} />
                  </div>

                  {/* Input */}
                  <div className="p-4 border-t border-slate-200">
                    <div className="flex gap-2">
                      <Input
                        value={newMessage}
                        onChange={e => setNewMessage(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
                        placeholder="Type a message..."
                        className="flex-1"
                        data-testid="message-input"
                      />
                      <Button onClick={handleSend} disabled={sending || !newMessage.trim()} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="send-message-btn">
                        <Send className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </>
              ) : (
                <div className="flex-1 flex items-center justify-center">
                  <div className="text-center">
                    <MessageSquare className="w-16 h-16 text-slate-200 mx-auto mb-4" />
                    <h3 className="font-outfit text-xl font-semibold text-slate-400">Select a conversation</h3>
                    <p className="text-sm text-slate-400 mt-1">Or message someone from their profile</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
